import { useRef, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, type Location } from "react-router";
import { twJoin } from "tailwind-merge";

import { useGetProfileSuspense, useListAccountEventsSuspense } from "@/api/bale";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/Button";
import { ContentLayout, ContentSubGrid } from "@/components/ContentLayout";
import { CreateEventModal } from "@/components/CreateEventModal";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { Icon } from "@/components/Icon";
import { useCurrentTime } from "@/hooks/useCurrentTime";

import { EventsTimeline } from "./components/EventsTimeline";
import { ProfileSetupModal } from "./components/ProfileSetupModal";

type LocationState = { showProfileSetup?: true };

export function ContentLeft({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <div className={twJoin("col-1 row-1", className)}>{children}</div>;
}

export function ContentRight({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <div className={twJoin("col-2 row-[1_/_3]", className)}>{children}</div>;
}

export function OverviewPage() {
  const { t } = useTranslation();
  const createEventModal = useRef<HTMLDialogElement>(null);
  const now = useCurrentTime("@daily");
  const navigate = useNavigate();
  // oxlint-disable-next-line no-unsafe-assignment
  const location: Location<LocationState | null> = useLocation();
  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });

  const eventsInProgress = useListAccountEventsSuspense(
    "@me",
    { event_status: ["NotReady"], limit: 1 },
    { query: { select: (data) => data.data } },
  );

  return (
    <FullScreenLayout>
      <ContentLayout title={t("upcoming-events")} subtitle={t("date", { date: now })}>
        <ContentSubGrid>
          <div className="col-2 row-span-full hidden bg-white shadow-md md:block" />
          <div className="flex grid-cols-subgrid flex-col content-start gap-y-8 py-4 md:col-[1_/_3] md:row-2 md:grid">
            {eventsInProgress.data.page_info.total_count > 0 && (
              <Link
                to={
                  eventsInProgress.data.page_info.total_count === 1
                    ? `/event/${eventsInProgress.data.events[0].event.id}`
                    : `/profile/${me.data.id}/events?tab=not_ready`
                }
              >
                <Banner className="max-w-180 font-interest">
                  <div className="flex flex-row items-center gap-4">
                    <p>
                      {t("you-have-unfinished-events", {
                        count: eventsInProgress.data.page_info.total_count,
                        name: eventsInProgress.data.events[0].event.name || t("untitled-event"),
                      })}
                    </p>
                    <Icon name="arrow-right" />
                  </div>
                </Banner>
              </Link>
            )}
            <div className="flex grid-cols-subgrid flex-col content-start gap-y-12 md:col-[1_/_3] md:grid md:gap-y-32">
              <EventsTimeline />
            </div>
          </div>
          <div className="sticky bottom-0 col-1 flex flex-col p-4 pb-[calc(env(safe-area-inset-bottom,_0)_+_1rem)] md:col-2">
            <Button
              type="button"
              onClick={() => createEventModal.current?.showModal()}
              className="shadow-lg md:shadow-none"
            >
              {t("host-an-event")}
            </Button>
          </div>
        </ContentSubGrid>
      </ContentLayout>

      {location.state?.showProfileSetup && (
        <ProfileSetupModal onComplete={() => navigate("/overview", { replace: true })} open />
      )}
      <CreateEventModal onClose={() => createEventModal.current?.close()} ref={createEventModal} />
    </FullScreenLayout>
  );
}
